import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import type { TreeItem } from "../primitives/types";
import { TreeDataContext } from "./tree-data-context";
import { type BranchHandlers, TreeRootContext } from "./tree-root-context";

export type TreeBranchContextValue = {
	parentId: string | null;
	items: TreeItem[];
	isLoading: boolean;
	createFolder: (folder: TreeItem) => void;
	deleteFolder: (folderId: string) => void;
};

export const TreeBranchContext = createContext<TreeBranchContextValue | null>(
	null,
);

export type TreeBranchProps = {
	id: string | null;
	onCreateFolder?: (parentBranchId: string | null, folder: TreeItem) => void;
	onDeleteFolder?: (folderId: string, branchId: string | null) => void;
	children: (items: TreeItem[], isLoading: boolean) => ReactNode;
};

export function TreeBranch({
	id,
	onCreateFolder,
	onDeleteFolder,
	children,
}: TreeBranchProps) {
	const rootContext = useContext(TreeRootContext);
	const useBranchItems = useContext(TreeDataContext);
	const { items, isLoading } = useBranchItems(id);

	const itemsRef = useRef(items);
	useEffect(() => {
		itemsRef.current = items;
	}, [items]);

	useEffect(() => {
		if (!rootContext) {
			return;
		}

		const handlers: BranchHandlers = {
			getItems: () => itemsRef.current,
			containsItem: (itemId: string) =>
				itemsRef.current.some((i) => i.id === itemId),
		};

		return rootContext.registerBranch(id, handlers);
	}, [rootContext, id]);

	const createFolder = useCallback(
		(folder: TreeItem) => {
			onCreateFolder?.(id, folder);
		},
		[id, onCreateFolder],
	);

	const deleteFolder = useCallback(
		(folderId: string) => {
			onDeleteFolder?.(folderId, id);
		},
		[id, onDeleteFolder],
	);

	const contextValue = useMemo<TreeBranchContextValue>(
		() => ({
			parentId: id,
			items,
			isLoading,
			createFolder,
			deleteFolder,
		}),
		[id, items, isLoading, createFolder, deleteFolder],
	);

	return (
		<TreeBranchContext.Provider value={contextValue}>
			{children(items, isLoading)}
		</TreeBranchContext.Provider>
	);
}

export function useTreeBranch() {
	const context = useContext(TreeBranchContext);
	if (!context) {
		throw new Error("useTreeBranch must be used within a TreeBranch");
	}
	return context;
}
